import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { getLscAdminIssues } from '../api';
import {
  LSC_SUPPORT_ISSUE_CATEGORIES,
  LSC_SUPPORT_ISSUE_SEVERITIES,
  LSC_SUPPORT_ISSUE_STATUSES,
  type LscAdminIssueListFilters,
  type LscAdminSupportIssue,
} from '../types';
import LoadingState from '../components/LoadingState';
import { lscPath } from '../routing';
import {
  adminIssueStatusLabel,
  compactLeagueId,
  LSC_ADMIN_ISSUE_EMPTY,
  LSC_ADMIN_ISSUE_QUEUE_INTRO,
  LSC_ADMIN_ISSUE_QUEUE_TITLE,
  parseAdminIssueError,
} from '../adminIssueCopy';
import { supportIssueCategoryLabel, supportIssueSeverityLabel } from '../supportIssueCopy';

const EMPTY_FILTERS: LscAdminIssueListFilters = {
  status: '',
  severity: '',
  category: '',
  league_id: '',
};

export function LscAdminIssuesPageView({
  issues,
  loading,
  error,
  filters,
  onFilterChange,
  onApplyFilters,
}: {
  issues: LscAdminSupportIssue[];
  loading: boolean;
  error: string | null;
  filters: LscAdminIssueListFilters;
  onFilterChange: (patch: Partial<LscAdminIssueListFilters>) => void;
  onApplyFilters: () => void;
}) {
  return (
    <div className="space-y-4" data-testid="lsc-admin-issues-page">
      <div>
        <h2 className="text-xl font-semibold text-white">{LSC_ADMIN_ISSUE_QUEUE_TITLE}</h2>
        <p className="text-sm text-secondary-400 mt-1">{LSC_ADMIN_ISSUE_QUEUE_INTRO}</p>
      </div>

      <form
        className="card grid max-w-lsc-narrow gap-3 sm:grid-cols-2 lg:grid-cols-5"
        onSubmit={(event) => {
          event.preventDefault();
          onApplyFilters();
        }}
        data-testid="lsc-admin-issue-filters"
      >
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary-500">Status</span>
          <select
            className="input-field"
            value={filters.status ?? ''}
            onChange={(e) => onFilterChange({ status: e.target.value as LscAdminIssueListFilters['status'] })}
            data-testid="lsc-admin-filter-status"
          >
            <option value="">All</option>
            {LSC_SUPPORT_ISSUE_STATUSES.map((status) => (
              <option key={status} value={status}>
                {adminIssueStatusLabel(status)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary-500">Severity</span>
          <select
            className="input-field"
            value={filters.severity ?? ''}
            onChange={(e) =>
              onFilterChange({ severity: e.target.value as LscAdminIssueListFilters['severity'] })
            }
            data-testid="lsc-admin-filter-severity"
          >
            <option value="">All</option>
            {LSC_SUPPORT_ISSUE_SEVERITIES.map((severity) => (
              <option key={severity} value={severity}>
                {supportIssueSeverityLabel(severity)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary-500">Category</span>
          <select
            className="input-field"
            value={filters.category ?? ''}
            onChange={(e) =>
              onFilterChange({ category: e.target.value as LscAdminIssueListFilters['category'] })
            }
            data-testid="lsc-admin-filter-category"
          >
            <option value="">All</option>
            {LSC_SUPPORT_ISSUE_CATEGORIES.map((category) => (
              <option key={category} value={category}>
                {supportIssueCategoryLabel(category)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm sm:col-span-2">
          <span className="text-secondary-500">League ID</span>
          <input
            className="input-field"
            value={filters.league_id ?? ''}
            onChange={(e) => onFilterChange({ league_id: e.target.value })}
            placeholder="Filter by league id"
            data-testid="lsc-admin-filter-league-id"
          />
        </label>
        <div className="sm:col-span-2 lg:col-span-5">
          <button type="submit" className="btn-secondary text-sm" data-testid="lsc-admin-filter-apply">
            Apply filters
          </button>
        </div>
      </form>

      {loading ? <LoadingState message="Loading support issues..." /> : null}

      {error ? (
        <p className="text-sm text-red-300" role="alert" data-testid="lsc-admin-issues-error">
          {error}
        </p>
      ) : null}

      {!loading && !error && issues.length === 0 ? (
        <p className="text-sm text-secondary-500" data-testid="lsc-admin-issues-empty">
          {LSC_ADMIN_ISSUE_EMPTY}
        </p>
      ) : null}

      {!loading && !error && issues.length > 0 ? (
        <div className="overflow-x-auto rounded-lg border border-secondary-700">
          <table className="min-w-full text-sm" data-testid="lsc-admin-issues-table">
            <thead className="bg-secondary-900/80 text-secondary-400">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Reference</th>
                <th className="px-3 py-2 text-left font-medium">Title</th>
                <th className="px-3 py-2 text-left font-medium">Category</th>
                <th className="px-3 py-2 text-left font-medium">Severity</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
                <th className="px-3 py-2 text-left font-medium">League</th>
                <th className="px-3 py-2 text-left font-medium">Reporter role</th>
                <th className="px-3 py-2 text-left font-medium">Updated</th>
              </tr>
            </thead>
            <tbody>
              {issues.map((issue) => (
                <tr
                  key={issue.issue_id}
                  className="border-t border-secondary-800 hover:bg-secondary-900/40"
                  data-testid="lsc-admin-issue-row"
                >
                  <td className="px-3 py-2">
                    <Link
                      to={lscPath(`/admin/issues/${issue.issue_id}`)}
                      className="text-primary-300 hover:text-primary-200"
                      data-testid="lsc-admin-issue-link"
                    >
                      {issue.issue_id}
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-white">{issue.title}</td>
                  <td className="px-3 py-2 text-secondary-300">
                    {supportIssueCategoryLabel(issue.category)}
                  </td>
                  <td className="px-3 py-2 text-secondary-300">
                    {supportIssueSeverityLabel(issue.severity)}
                  </td>
                  <td className="px-3 py-2 text-secondary-300">{adminIssueStatusLabel(issue.status)}</td>
                  <td className="px-3 py-2 text-secondary-400">
                    {issue.league_id ? (
                      <Link
                        to={lscPath(`/admin/leagues/${issue.league_id}`)}
                        className="text-primary-300 hover:text-primary-200"
                        data-testid="lsc-admin-issue-league-link"
                      >
                        {compactLeagueId(issue.league_id)}
                      </Link>
                    ) : (
                      compactLeagueId(issue.league_id)
                    )}
                  </td>
                  <td className="px-3 py-2 text-secondary-400">{issue.reporter_role || '—'}</td>
                  <td className="px-3 py-2 text-secondary-500">{issue.updated_at || issue.created_at || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

const LscAdminIssuesPage: React.FC = () => {
  const [issues, setIssues] = useState<LscAdminSupportIssue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<LscAdminIssueListFilters>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<LscAdminIssueListFilters>(EMPTY_FILTERS);

  const loadIssues = useCallback(async (nextFilters: LscAdminIssueListFilters) => {
    setLoading(true);
    setError(null);
    try {
      const rows = await getLscAdminIssues(nextFilters);
      setIssues(rows);
    } catch (err) {
      setError(parseAdminIssueError(err));
      setIssues([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadIssues(appliedFilters);
  }, [appliedFilters, loadIssues]);

  return (
    <LscAdminIssuesPageView
      issues={issues}
      loading={loading}
      error={error}
      filters={filters}
      onFilterChange={(patch) => setFilters((current) => ({ ...current, ...patch }))}
      onApplyFilters={() => setAppliedFilters({ ...filters })}
    />
  );
};

export default LscAdminIssuesPage;
